import { useTenantAvailable } from '@api/application-configuration';
import {
  Goal,
  LayoutDashboard,
  List,
  PieChart,
  PlusCircle,
  RefreshCcw,
  Settings2,
  Share2
} from '@tamagui/lucide-icons';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import { Route } from '@unistocks/features/navigation/route';
import { useDefaultPortfollioRoute } from '@unistocks/features/portfolios/use-route';
import { useDefaultSavingsGoalsRoute } from '@unistocks/features/savings-goals/use-route';
import { useDefaultWatchlistRoute } from '@unistocks/features/watchlists/use-route';
import {
  usePortfoliosGetListQuery
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  useSavingsGoalsGetListQuery
} from '@unistocks/proxy/axios-client/SavingsGoalsQuery';
import {
  useWatchlistsGetListQuery
} from '@unistocks/proxy/axios-client/WatchlistsQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import { Href } from 'expo-router';

export const useAppRoutes = (): Route[] => {
  const { data: portfolios, isSuccess: isSuccessPortfolios } = usePortfoliosGetListQuery();
  const defaultPortfolioRoute = useDefaultPortfollioRoute();

  const { data: watchlist, isSuccess: isSuccessWatchlists } = useWatchlistsGetListQuery();
  const defaultWatchlistRoute = useDefaultWatchlistRoute();

  const { data: savingsGoal, isSuccess: isSuccessSavingsGoal } = useSavingsGoalsGetListQuery();
  const defaultSavingsGoalRoute = useDefaultSavingsGoalsRoute();

  return [
    {
      key: 'index',
      rootPathName: 'index',
      name: t('::Menu:Dashboard'),
      href: '/',
      Icon: props => <LayoutDashboard {...props} />
    },
    {
      key: 'portfolio',
      rootPathName: 'portfolios',
      name: t('::Menu:Portfolios'),
      href: defaultPortfolioRoute,
      Icon: props => <PieChart {...props} />,
      children: [
        ...(isSuccessPortfolios
          ? portfolios.items.map(
              ({ id, displayName }) =>
                ({
                  key: `portfolio-${id}`,
                  rootPathName: 'portfolios',
                  name: displayName,
                  href: { pathname: '/(tabs)/portfolios/[id]', params: { id } },
                  Icon: ({ size }) => <UnistocksCircle size={size} />
                }) as Route
            )
          : []),
        {
          key: 'portfolio-create',
          rootPathName: 'portfolios',
          name: t('::Menu:Portfolios:Create'),
          href: '/(tabs)/portfolios/create',
          Icon: ({ size }) => <PlusCircle color={'$success'} size={size} />,
          iconProps: { color: '$success' }
        }
      ]
    },
    {
      key: 'watchlists',
      rootPathName: 'watchlists',
      name: t('::Menu:Watchlists'),
      href: defaultWatchlistRoute,
      Icon: props => <List {...props} />,
      children: [
        ...(isSuccessWatchlists
          ? watchlist.items.map(
              ({ id, displayName }) =>
                ({
                  key: `watchlist-${id}`,
                  rootPathName: 'watchlists',
                  name: displayName,
                  href: { pathname: '/(tabs)/watchlists/[id]', params: { id } },
                  Icon: ({ size }) => <UnistocksCircle size={size} />
                }) as Route
            )
          : []),
        {
          key: 'watchlist-create',
          rootPathName: 'watchlists',
          name: t('::Menu:Watchlists:Create'),
          href: '/(tabs)/watchlists/create',
          Icon: props => <PlusCircle {...props} />,
          iconProps: { color: '$success' }
        }
      ]
    },
    {
      key: 'savings-goals',
      rootPathName: '(menu)',
      name: t('::Menu:SavingsGoals'),
      href: defaultSavingsGoalRoute,
      Icon: props => <Goal {...props} />,
      children: [
        ...(isSuccessSavingsGoal
          ? savingsGoal.items.map(
              ({ id, displayName }) =>
                ({
                  key: `savings-goal-${id}`,
                  rootPathName: '(menu)',
                  name: displayName,
                  href: { pathname: '/(tabs)/(menu)/savings-goals/[id]', params: { id } },
                  Icon: ({ size }) => <UnistocksCircle size={size} />
                }) as Route
            )
          : []),
        {
          key: 'savings-goal-create',
          rootPathName: '(menu)',
          name: t('::Menu:SavingsGoals:Create'),
          href: '/(tabs)/(menu)/savings-goals/create',
          Icon: props => <PlusCircle {...props} />,
          iconProps: { color: '$success' }
        }
      ]
    } as Route
  ];
};

export const useSecondaryRoutes = (): Route[] => {
  const isTenantUser = useTenantAvailable();

  const settingsHref: Href = isTenantUser ? '/(tabs)/(menu)/settings' : '/(admin)/(menu)/settings';

  const routes: Route[] = [
    {
      key: 'settings',
      rootPathName: 'settings',
      name: t('::Menu:Settings'),
      href: settingsHref,
      Icon: props => <Settings2 {...props} />
    }
  ];

  if (isTenantUser) {
    routes.push(
      {
        key: 'external-accounts',
        rootPathName: 'external-accounts',
        name: t('::ExternalAccounts'),
        href: '/(tabs)/(menu)/external-accounts',
        Icon: props => <RefreshCcw {...props} />
      },
      {
        key: 'shared-portfolios',
        rootPathName: 'shared-portfolios',
        name: t('::Menu:SharedPortfolios'),
        href: '/(tabs)/(menu)/shared-portfolios',
        Icon: props => <Share2 {...props} />
      }
    );
  }

  return routes;
};
